import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMedia, upsertMedia, uploadImage } from '@/lib/api/media';
import { logActivity as logActivityDb } from '@/lib/api/activity';

interface MediaImage {
  id: string;
  name: string;
  url: string;
  location: string;
  description: string;
}

const DEFAULT_IMAGES: MediaImage[] = [
  { id: 'img-1', name: 'Hero-Bild Startseite', url: '', location: 'Startseite Hero', description: 'Hintergrundbild des Hero-Bereichs auf der Startseite' },
  { id: 'img-2', name: 'Klinik-Gebäude', url: '', location: 'Über uns', description: 'Aussenansicht der Esteva Clinic in Tirana' },
  { id: 'img-3', name: 'Behandlungsraum Dental', url: '', location: 'Leistungen', description: 'Moderner Dental-Behandlungsraum' },
  { id: 'img-4', name: 'Behandlungsraum Hair', url: '', location: 'Leistungen', description: 'Haartransplantations-Behandlungsraum' },
  { id: 'img-5', name: 'Team-Foto Gruppe', url: '', location: 'Team', description: 'Team-Gruppenfoto auf der Team-Seite' },
  { id: 'img-6', name: 'Galerie Before 1', url: '', location: 'Galerie', description: 'Vorher-Bild Zahnbehandlung 1' },
  { id: 'img-7', name: 'Galerie After 1', url: '', location: 'Galerie', description: 'Nachher-Bild Zahnbehandlung 1' },
  { id: 'img-8', name: 'Galerie Before Hair', url: '', location: 'Galerie', description: 'Vorher-Bild Haartransplantation' },
  { id: 'img-9', name: 'Galerie After Hair', url: '', location: 'Galerie', description: 'Nachher-Bild Haartransplantation' },
];

export default function MediaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [images, setImages] = useState<MediaImage[]>([]);
  const [editing, setEditing] = useState<MediaImage | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadImages();
  }, [navigate]);

  const loadImages = async () => {
    try {
      const rows = await getMedia();
      const byKey = new Map(rows.map((r) => [r.media_key ?? '', r]));
      // Start from the fixed slot template, overlay any saved values from the DB.
      setImages(DEFAULT_IMAGES.map((slot) => {
        const r = byKey.get(slot.id);
        return r ? { ...slot, url: r.url ?? '', location: r.page ?? slot.location, description: r.alt ?? slot.description } : slot;
      }));
    } catch {
      setImages(DEFAULT_IMAGES);
    }
  };

  const handleEdit = (img: MediaImage) => {
    setEditing({ ...img });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      await upsertMedia({
        media_key: editing.id,
        url: editing.url,
        alt: editing.description,
        page: editing.location,
      });
      await logActivityDb('media_updated', `Bild "${editing.name}" aktualisiert`);
      setImages(images.map((img) => (img.id === editing.id ? editing : img)));
      setSavedMsg(t('adm2.saved'));
    } catch {
      setSavedMsg(t('adm2.save_error'));
    }
    setTimeout(() => setSavedMsg(''), 2000);
    setShowModal(false);
    setEditing(null);
  };

  const handleFileUpload = async (file: File, callback: (url: string) => void) => {
    if (file.size > 5 * 1024 * 1024) {
      alert(t('adm2.file_too_large'));
      return;
    }
    try {
      setSavedMsg(t('adm2.uploading'));
      const url = await uploadImage(file);
      callback(url);
      setSavedMsg('');
    } catch {
      setSavedMsg(t('adm2.upload_failed'));
    }
  };

  const locations = [...new Set(images.map((img) => img.location))];
  const filtered = filterLocation === 'all' ? images : images.filter((img) => img.location === filterLocation);

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-900">{t('adm2.media_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">
            {t('adm2.media_uploaded_count', { uploaded: images.filter((img) => img.url).length, total: images.length })}
          </p>
        </div>
        {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
      </div>

      {/* ── Location Filter ─────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilterLocation('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
            filterLocation === 'all' ? 'bg-foreground-900 text-white' : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
          }`}
        >
          {t('adm2.filter_all')}
        </button>
        {locations.map((loc) => (
          <button
            key={loc}
            onClick={() => setFilterLocation(loc)}
            className={`px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              filterLocation === loc ? 'bg-accent-500 text-white' : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {loc}
          </button>
        ))}
      </div>

      {/* ── Image Grid ──────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((img) => (
          <div key={img.id} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden group">
            <div className="aspect-video bg-background-100 overflow-hidden relative">
              {img.url ? (
                <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                  <i className="ri-image-add-line text-2xl text-foreground-300"></i>
                  <span className="text-[10px] text-foreground-400 font-label">{t('adm2.no_image')}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                <button
                  onClick={() => handleEdit(img)}
                  className="opacity-0 group-hover:opacity-100 px-3 py-1.5 rounded-lg bg-white/90 text-foreground-900 text-[10px] font-label transition-all cursor-pointer whitespace-nowrap"
                >
                  {img.url ? t('adm2.edit') : t('adm2.upload')}
                </button>
              </div>
            </div>
            <div className="p-3">
              <p className="text-xs font-label font-semibold text-foreground-900 truncate">{img.name}</p>
              <p className="text-[10px] text-foreground-400 font-body mt-0.5">{img.location} · {img.description}</p>
              {!img.url && (
                <p className="text-[10px] text-accent-600 font-body mt-1 flex items-center gap-1">
                  <i className="ri-upload-cloud-line"></i>
                  {t('adm2.image_required')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Edit Modal ──────────────────────────────── */}
      {showModal && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-background-100 flex items-center justify-between">
              <h2 className="font-label text-sm font-semibold text-foreground-900">{editing.url ? t('adm2.edit_image') : t('adm2.upload_image')}</h2>
              <button onClick={() => { setShowModal(false); setEditing(null); }} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="aspect-video bg-background-100 rounded-lg overflow-hidden flex items-center justify-center">
                {editing.url ? (
                  <img src={editing.url} alt={editing.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <i className="ri-image-add-line text-3xl text-foreground-300"></i>
                    <p className="text-[10px] text-foreground-400 font-label mt-2">{t('adm2.no_image_uploaded')}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm2.upload_image')}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, (url) => setEditing({ ...editing, url }));
                  }}
                  className="block w-full text-xs text-foreground-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-100 file:text-primary-700 file:font-label hover:file:bg-primary-200 cursor-pointer"
                />
                <p className="text-[10px] text-foreground-400 font-body mt-1">{t('adm2.max_5mb_jpg_png')}</p>
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm2.image_url_optional')}</label>
                <input type="text" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" placeholder="https://..." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm2.location')}</label>
                  <input type="text" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm2.description')}</label>
                  <input type="text" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-background-100 flex items-center justify-end gap-3">
              <button onClick={() => { setShowModal(false); setEditing(null); }} className="px-4 py-2 rounded-lg text-xs font-label text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">{t('adm2.cancel')}</button>
              <button onClick={handleSave} className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">{t('adm2.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}